import React, { forwardRef, LiHTMLAttributes, useId } from 'react';
import { buildCSSWithLength, CSSValueWithLength, getAutoColorCode } from '../../styles';
import styled from '@emotion/styled';
import { semantic_colors, shapes, spacing, text_styles } from '../../foundation';
import { IconArrowChevronRight } from '../icons/generated';
import { getInputHeightBySize, getInputSpacingBySize } from '../input/styles';
import { DropdownCompositeSize } from './types';
import { getDropdownCompositeOptionWidth } from './styles';

export interface DropdownCompositeItemProps extends LiHTMLAttributes<HTMLLIElement> {
  disabled?: boolean;
  /**
   *  @default false
   *  Option 선택 여부
   */
  selected?: boolean;
  /**
   *  @default false
   *  Option focus 여부
   */
  focused?: boolean;
  width?: CSSValueWithLength;
  optionItemHeight?: number;
  /** @default 'medium' */
  size?: DropdownCompositeSize;
}

export const DropdownCompositeItem = forwardRef<HTMLLIElement, DropdownCompositeItemProps>(
  ({ children, disabled = false, selected = false, width, optionItemHeight, size = 'medium', ...props }, ref) => {
    const id = useId();
    const defaultWidth = getDropdownCompositeOptionWidth(size);
    const isPlainTextLabel = typeof children === 'string' || typeof children === 'number';

    return (
      <SC.DropdownCompositeItem
        width={width ?? defaultWidth}
        disabled={disabled}
        selected={selected}
        height={optionItemHeight ?? getInputHeightBySize(size)}
        size={size}
        role='option'
        id={`pds-dropdown-composite-item-${id}`}
        {...props}
        ref={ref}
      >
        {isPlainTextLabel ? <SC.Label>{children}</SC.Label> : children}
        <SC.Icon
          size={size === 'xsmall' ? 14 : 16}
          color={selected ? 'currentColor' : semantic_colors.content.quaternary}
        />
      </SC.DropdownCompositeItem>
    );
  },
);

const SC = {
  DropdownCompositeItem: styled.li<{
    disabled?: boolean;
    selected?: boolean;
    focused?: boolean;
    width?: CSSValueWithLength;
    height?: number;
    size: DropdownCompositeSize;
  }>`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    ${({ height }) => buildCSSWithLength('height', height)};
    ${({ width }) => buildCSSWithLength('width', width)};
    padding: 0 ${spacing.spacing_2} 0 ${spacing.spacing_3};
    gap: ${({ size }) => getInputSpacingBySize(size)}px;
    ${text_styles.Body_13_Regular};
    cursor: pointer;
    ${shapes.border_radius.xsmall};
    background-color: ${semantic_colors.background.surface};
    color: ${semantic_colors.content.primary};
    outline: none;
    transition: all 0.1s linear;

    &:hover {
      background-color: ${getAutoColorCode(semantic_colors.background.surface).hover};
    }
    &:active {
      background-color: ${getAutoColorCode(semantic_colors.background.surface).pressed};
    }
    ${({ disabled }) =>
      disabled &&
      `
        cursor: not-allowed;
        opacity: 0.4;
      `}
    ${({ selected }) =>
      selected &&
      `
        background-color: ${semantic_colors.accent.light};
        color: ${semantic_colors.accent.primary};
        &:hover {
          background-color: ${getAutoColorCode(semantic_colors.accent.light).hover};
        }
        &:active {
          background-color: ${getAutoColorCode(semantic_colors.accent.light).pressed};
        }
      `}
  `,
  Label: styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  Icon: styled(IconArrowChevronRight)`
    flex-shrink: 0;
  `,
};
